import {
  NAME_MIN,
  NAME_MAX,
  C3D_EYES,
  C3D_HAIR,
  C3D_HAIR_COLORS,
  C3D_SKIN_TONES,
  DEFAULT_APPEARANCE,
  type CharacterAppearance,
  type CharacterDTO,
} from "@ghostland/shared";
import { createCharacter, updateCharacter } from "../characters/api.js";
import { attachIdlePreview } from "../sprites/idlePreview.js";
import { injectTheme } from "./theme.js";
import { mountGhostLogo } from "./ghostLogo.js";

const STYLE_ID = "ghostland-charcreate-style";

/**
 * Character creator — GhostLand design ideology: clean, minimal, spacious, Yohji
 * Yamamoto in mind. Solid black stage, monochrome (black / white / gray) — no gold,
 * no torches; the animated character is the centrepiece and every selection reflects
 * on it immediately. The staggered "GHOST LAND" wordmark sits up top.
 */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-ce-overlay {
  position: fixed; inset: 0; z-index: 12;
  display: flex; align-items: stretch; justify-content: center;
  background: #000; color: #f4f4f4;
}
/* this screen is intentionally PIXEL-styled (overrides the global sans face) */
#gl-ce-overlay, #gl-ce-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }

/* left: the character, centred in the space below the title (top padding clears the
   wordmark). A soft grayscale vignette gives it depth. */
#gl-ce-stage {
  flex: 1; position: relative;
  display: flex; align-items: center; justify-content: center;
  padding: 120px 48px 56px; margin: 30px;
  background: radial-gradient(ellipse 58% 48% at 50% 44%, rgba(255,255,255,.05), transparent 70%);
}
#gl-ce-preview { image-rendering: pixelated; width: 400px; height: 400px; }

/* right: the customization options */
#gl-ce-panel {
  width: 360px; display: flex; flex-direction: column; justify-content: center;
  gap: 28px; padding: 56px 52px 56px 12px;
}
.gl-ce-group { display: flex; flex-direction: column; gap: 12px; }
.gl-ce-label {
  font-size: 13px; letter-spacing: 0.3em; color: #b8b8b8; text-transform: uppercase;
}
.gl-ce-control {
  display: flex; align-items: center; gap: 14px;
  border: 2px solid #262626; background: #0a0a0a; padding: 8px 12px;
}
.gl-ce-step {
  background: none; border: none; color: #f4f4f4; cursor: pointer;
  font-size: 18px; padding: 2px 8px; line-height: 1;
}
.gl-ce-step:hover:not(:disabled) { color: #fff; }
.gl-ce-step:disabled { color: #2e2e2e; cursor: default; }
.gl-ce-value { flex: 1; text-align: center; font-size: 15px; letter-spacing: 0.06em; color: #d8d8d8; }
.gl-ce-muted { color: #4a4a4a; }
#gl-ce-name {
  width: 100%; box-sizing: border-box; padding: 14px 12px;
  background: #0a0a0a; border: 2px solid #262626;
  color: #f4f4f4; font-size: 16px; letter-spacing: 0.06em;
}
#gl-ce-name:focus { outline: none; border-color: #f4f4f4; }
#gl-ce-name::placeholder { color: #4a4a4a; }
#gl-ce-actions { display: flex; flex-direction: column; gap: 12px; margin-top: 4px; }
.gl-ce-btn {
  border: 2px solid #fff; background: #000; color: #fff; padding: 14px 12px; cursor: pointer;
  text-align: center; font-size: 14px; letter-spacing: 0.16em; text-transform: uppercase;
}
.gl-ce-btn.primary { background: #f4f4f4; border-color: #fff; color: #000; }
.gl-ce-btn.primary:hover:not(:disabled) { background: #fff; }
.gl-ce-btn.primary:disabled { opacity: 0.35; cursor: default; }
.gl-ce-btn.ghost { background: #000; border-color: #3a3a3a; color: #8a8a8a; }
.gl-ce-btn.ghost:hover { border-color: #5a5a5a; color: #f4f4f4; }
#gl-ce-error { min-height: 14px; font-size: 12px; color: var(--gl-error); letter-spacing: 0.05em; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

interface EditorOptions {
  /** Edit an existing character; omit for a brand-new one. */
  character?: CharacterDTO;
}

export function showCharacterEditor(opts: EditorOptions = {}): Promise<CharacterDTO | null> {
  ensureStyle();
  const editing = opts.character;
  const a: CharacterAppearance = { ...DEFAULT_APPEARANCE, ...(editing?.appearance ?? {}) };

  return new Promise<CharacterDTO | null>((resolve) => {
    const overlay = document.createElement("div");
    overlay.id = "gl-ce-overlay";
    const hairAvailable = Object.keys(C3D_HAIR).length > 0;
    overlay.innerHTML = `
      <div id="gl-ce-stage">
        <canvas id="gl-ce-preview" width="432" height="432"></canvas>
      </div>
      <div id="gl-ce-panel">
        <div class="gl-ce-group">
          <span class="gl-ce-label">Skin</span>
          <div class="gl-ce-control" data-row="skin">
            <button class="gl-ce-step" data-dir="-1" type="button">‹</button>
            <span class="gl-ce-value"></span>
            <button class="gl-ce-step" data-dir="1" type="button">›</button>
          </div>
        </div>
        <div class="gl-ce-group">
          <span class="gl-ce-label">Eyes</span>
          <div class="gl-ce-control" data-row="eyes">
            <button class="gl-ce-step" data-dir="-1" type="button">‹</button>
            <span class="gl-ce-value"></span>
            <button class="gl-ce-step" data-dir="1" type="button">›</button>
          </div>
        </div>
        <div class="gl-ce-group">
          <span class="gl-ce-label">Hair</span>
          <div class="gl-ce-control" data-row="hair">
            <button class="gl-ce-step" data-dir="-1" type="button" ${hairAvailable ? "" : "disabled"}>‹</button>
            <span class="gl-ce-value ${hairAvailable ? "" : "gl-ce-muted"}"></span>
            <button class="gl-ce-step" data-dir="1" type="button" ${hairAvailable ? "" : "disabled"}>›</button>
          </div>
        </div>
        <div class="gl-ce-group">
          <span class="gl-ce-label">Hair colour</span>
          <div class="gl-ce-control" data-row="hairColor">
            <button class="gl-ce-step" data-dir="-1" type="button" ${hairAvailable ? "" : "disabled"}>‹</button>
            <span class="gl-ce-value ${hairAvailable ? "" : "gl-ce-muted"}"></span>
            <button class="gl-ce-step" data-dir="1" type="button" ${hairAvailable ? "" : "disabled"}>›</button>
          </div>
        </div>
        <div class="gl-ce-group">
          <input id="gl-ce-name" type="text" maxlength="${NAME_MAX}" placeholder="Name" autocomplete="off" />
          <div id="gl-ce-actions">
            <button class="gl-ce-btn primary" id="gl-ce-save" type="button">${editing ? "Save" : "Create"}</button>
            <button class="gl-ce-btn ghost" id="gl-ce-cancel" type="button">Cancel</button>
          </div>
          <div id="gl-ce-error"></div>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const logo = mountGhostLogo(overlay);

    const previewCanvas = overlay.querySelector<HTMLCanvasElement>("#gl-ce-preview")!;
    const nameInput = overlay.querySelector<HTMLInputElement>("#gl-ce-name")!;
    const saveBtn = overlay.querySelector<HTMLButtonElement>("#gl-ce-save")!;
    const errorEl = overlay.querySelector<HTMLDivElement>("#gl-ce-error")!;
    if (editing) nameInput.value = editing.name;

    const preview = attachIdlePreview(previewCanvas, a);

    const eyeIds = Object.keys(C3D_EYES).map(Number);
    const hairIds = [0, ...Object.keys(C3D_HAIR).map(Number)];
    const skinIds = C3D_SKIN_TONES.map((_, i) => i);

    function cycle(list: number[], cur: number, dir: number): number {
      const i = list.indexOf(cur);
      return list[(i + dir + list.length) % list.length];
    }

    function render(): void {
      const set = (row: string, text: string): void => {
        overlay.querySelector(`[data-row="${row}"] .gl-ce-value`)!.textContent = text;
      };
      set("skin", C3D_SKIN_TONES[a.skinTone]?.name ?? "—");
      set("eyes", C3D_EYES[a.face]?.label ?? "—");
      set("hair", hairAvailable ? (a.hair === 0 ? "None" : C3D_HAIR[a.hair]?.label ?? "—") : "Soon");
      set("hairColor", hairAvailable
        ? (a.hair === 0 ? "—" : C3D_HAIR_COLORS[a.hairColor]?.name ?? "—")
        : "Soon");
      preview.setAppearance(a);
    }

    overlay.querySelectorAll<HTMLButtonElement>(".gl-ce-step").forEach((btn) => {
      btn.addEventListener("click", () => {
        const dir = Number(btn.dataset.dir) as 1 | -1;
        const row = btn.closest("[data-row]")!.getAttribute("data-row");
        if (row === "skin") a.skinTone = cycle(skinIds, a.skinTone, dir);
        else if (row === "eyes") a.face = cycle(eyeIds, a.face, dir);
        else if (row === "hair") a.hair = cycle(hairIds, a.hair, dir);
        else if (row === "hairColor" && a.hair > 0)
          a.hairColor = (a.hairColor + dir + C3D_HAIR_COLORS.length) % C3D_HAIR_COLORS.length;
        render();
      });
    });
    render();

    function close(result: CharacterDTO | null): void {
      preview.dispose();
      logo.dispose();
      overlay.remove();
      resolve(result);
    }

    async function onSave(): Promise<void> {
      const name = nameInput.value.trim();
      if (name.length < NAME_MIN || name.length > NAME_MAX) {
        errorEl.textContent = `Name must be ${NAME_MIN}–${NAME_MAX} characters.`;
        return;
      }
      saveBtn.disabled = true;
      errorEl.textContent = "";
      try {
        const character = editing
          ? await updateCharacter(editing.characterId!, { name, appearance: { ...a } })
          : await createCharacter(name, { ...a });
        close(character);
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : "Something went wrong.";
        saveBtn.disabled = false;
      }
    }

    saveBtn.addEventListener("click", onSave);
    overlay.querySelector<HTMLButtonElement>("#gl-ce-cancel")!.addEventListener("click", () => close(null));
    nameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void onSave();
    });
    nameInput.focus();
  });
}
